import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { DateTime } from 'luxon';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { Course } from '../courses/Course.js';
import { ChancenEventlog } from './ChancenEventlog.js';

@Entity()
export class AttendanceExportEventlog extends ChancenEventlog {
  static override _class = 'AttendanceExportEventlog';

  constructor(doc: PartialEntityData<AttendanceExportEventlog>) {
    super(doc);
  }

  // userId is supplied by EventLog object

  @Field(type => String) kpfID: Maybe<string>;

  @Field(type => [String], { default: v => v ?? [] }) courseIDs: Maybe<string[]>;

  @Field(type => String) type: Maybe<string>;

  // Which action has been performed that lead to an export, e.g. "Anwesenheit" or "Kommentare & Entschuldigung" button has been pressed
  @validate(nonOptional())
  @Field(type => String)
  action!: string;

  @validate(nonOptional())
  @Field(type => DateTime)
  startDate!: DateTime;

  @validate(nonOptional())
  @Field(type => DateTime)
  endDate!: DateTime;

  @DataLoadedField(type => ChancenwerkUser, ({ userID }) => userID)
  user: Maybe<ChancenwerkUser>;

  @DataLoadedField(type => KPF, ({ kpfID }) => kpfID)
  kpf: Maybe<KPF>;

  @DataLoadedField(type => [Course], ({ courseIDs }) => courseIDs ?? [])
  courses: Maybe<Course[]>;

  @Mutation(type => String)
  public static safeCreate(@Arg('entity', type => AttendanceExportEventlog) entity: AttendanceExportEventlog) {
    if (!entity.kpfID) throw new Error('Kpf not declared!');

    return async (gqlFields: void) => {
      if (context.serverContext) {
        throw new Error('Not available in Server Context');
      }

      entity.userID = context.userID;
      const result = await entity.create();
      return result;
    };
  }

  @Query(type => [AttendanceExportEventlog])
  public static getEntitiesByKpf(@Arg('kpfID', type => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<AttendanceExportEventlog>) => {
      const col = await getCollection<AttendanceExportEventlog>(AttendanceExportEventlog._class);

      return col.find({ kpfID }).toArray();
    };
  }

  public static attendanceExportDefaultSet: BaseQLSelectionSet<AttendanceExportEventlog> = {
    user: {
      username: 1,
      vorname: 1,
      nachname: 1,
      email: 1,
    },
    kpf: {
      kuerzel: 1,
      school: {
        name: 1,
      },
    },
    courses: {
      name: 1,
      location: 1,
      weekday: 1,
      startTime: 1,
    },
    action: 1,
    type: 1,
    startDate: 1,
    endDate: 1,
    createdAt: 1,
  };
}

if (A.isServer) {
  const attendanceEventLogs = getRawCollection<any>(AttendanceExportEventlog._collectionName);

  // Create DB Indices
  void attendanceEventLogs.then(async db => {
    void db.createIndex({ kpfID: 1 });
  });
}
